import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Modal } from 'react-bootstrap';
import axios from 'axios';
import TissBillingPanel from './TissBillingPanel';

/* ── ícone reutilizável ── */
const Icon = ({ d, size = 16, color = 'currentColor', stroke = 2 }) => (
  <svg width={size} height={size} fill="none" stroke={color} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={stroke} d={d} />
  </svg>
);

const ICONS = {
  plus:     'M12 4v16m8-8H4',
  edit:     'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  chevron:  'M19 9l-7 7-7-7',
  plan:     'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707..414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  shield:   'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  warn:     'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  info:     'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
};

/* ── helpers de estilo ── */
const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: '8px',
  border: '1px solid #e5e7eb', fontSize: `calc(13px * var(--font-scale))`, color: '#374151',
  fontFamily: "'Inter','Segoe UI',sans-serif", outline: 'none',
  backgroundColor: '#fff', boxSizing: 'border-box',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};
const focusHandlers = {
  onFocus: e => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; },
  onBlur:  e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; },
};

const FieldLabel = ({ children, required }) => (
  <label style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '5px' }}>
    {children}{required && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
  </label>
);

/* ── accordion item ── */
const AccordionItem = ({ title, badge, children, defaultOpen = false, accentColor = '#2563eb' }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '8px' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', border: 'none', cursor: 'pointer',
          background: open ? `linear-gradient(135deg, ${accentColor}10, ${accentColor}05)` : '#f9fafb',
          fontFamily: 'inherit', transition: 'background 0.15s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', color: '#111827' }}>{title}</span>
          {badge && (
            <span style={{
              fontSize: `calc(10px * var(--font-scale))`, fontWeight: '700', padding: '1px 7px', borderRadius: '999px',
              backgroundColor: `${accentColor}15`, color: accentColor, border: `1px solid ${accentColor}30`,
            }}>{badge}</span>
          )}
        </div>
        <div style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
          <Icon d={ICONS.chevron} size={14} color="#6b7280" />
        </div>
      </button>
      {open && (
        <div style={{ padding: '16px', backgroundColor: '#fff', borderTop: '1px solid #f1f5f9' }}>
          {children}
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════ */

const ConveniosTab = ({ currentEntidade, isActive, formatCurrency }) => {
  const [convenios, setConvenios]   = useState([]);
  const [servicos,  setServicos]    = useState([]);
  const [isLoading, setIsLoading]   = useState(false);
  const [error,     setError]       = useState(null);

  const [showConvenioModal,  setShowConvenioModal]  = useState(false);
  const [showPlanoModal,     setShowPlanoModal]     = useState(false);
  const [showCoberturaModal, setShowCoberturaModal] = useState(false);

  const [currentConvenio,  setCurrentConvenio]  = useState(null);
  const [currentPlano,     setCurrentPlano]     = useState(null);
  const [currentCobertura, setCurrentCobertura] = useState(null);

  const [modalError, setModalError] = useState(null);
  const [isSaving,   setIsSaving]   = useState(false);

  const fetchData = useCallback(async () => {
    if (!isActive || !currentEntidade?.id) return;
    setIsLoading(true); setError(null);
    try {
      const [conveniosRes, servicosRes] = await Promise.all([
        axios.get(`/api/convenios?entidade_id=${currentEntidade.id}`),
        axios.get(`/api/servicos?entidade_id=${currentEntidade.id}`),
      ]);
      setConvenios(conveniosRes.data.convenios || []);
      setServicos(servicosRes.data.servicos    || []);
    } catch (err) {
      setError('Falha ao carregar dados de convênios e serviços.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [isActive, currentEntidade?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── save helpers ── */
  const saveItem = async (url, method, data) => {
    setIsSaving(true); setModalError(null);
    try {
      await axios({ method, url, data });
      return true;
    } catch (err) {
      setModalError(err.response?.data?.message || err.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  /* ── Convênio ── */
  const handleOpenConvenioModal = (convenio = null) => {
    setModalError(null);
    setCurrentConvenio(convenio || { nome: '', cnpj: '', registro_ans: '', ativo: true, entidade_id: currentEntidade.id });
    setShowConvenioModal(true);
  };
  const handleSaveConvenio = async () => {
    const ok = await saveItem(
      currentConvenio.id ? `/api/convenios/${currentConvenio.id}` : '/api/convenios',
      currentConvenio.id ? 'put' : 'post',
      currentConvenio,
    );
    if (ok) { setShowConvenioModal(false); fetchData(); }
  };

  /* ── Plano ── */
  const handleOpenPlanoModal = (convenioId, plano = null) => {
    setModalError(null);
    setCurrentPlano(plano || { nome: '', descricao: '', ativo: true, convenio_id: convenioId });
    setShowPlanoModal(true);
  };
  const handleSavePlano = async () => {
    const ok = await saveItem(
      currentPlano.id ? `/api/convenio-planos/${currentPlano.id}` : '/api/convenio-planos',
      currentPlano.id ? 'put' : 'post',
      currentPlano,
    );
    if (ok) { setShowPlanoModal(false); fetchData(); }
  };

  /* ── Cobertura ── */
  const handleOpenCoberturaModal = (planoId, cobertura = null) => {
    setModalError(null);
    setCurrentCobertura(cobertura || {
      servico_id: '', tipo_cobertura: 'percentual', valor_cobertura: 0,
      tipo_coparticipacao: 'percentual', valor_coparticipacao: 0,
      convenio_plano_id: planoId,
    });
    setShowCoberturaModal(true);
  };
  const handleSaveCobertura = async () => {
    if (!currentCobertura.servico_id) { setModalError('Selecione um serviço.'); return; }
    const ok = await saveItem(
      currentCobertura.id ? `/api/convenio-coberturas/${currentCobertura.id}` : '/api/convenio-coberturas',
      currentCobertura.id ? 'put' : 'post',
      currentCobertura,
    );
    if (ok) { setShowCoberturaModal(false); fetchData(); }
  };

  /* ── ESTADOS DE CARREGAMENTO / ERRO ── */
  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px', gap: '12px' }}>
        <Spinner animation="border" variant="primary" style={{ width: '32px', height: '32px' }} />
        <p style={{ margin: 0, fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280' }}>Carregando convênios...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '16px 20px', borderRadius: '10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <Icon d={ICONS.warn} size={16} color="#dc2626" />
        <span style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#dc2626' }}>{error}</span>
      </div>
    );
  }

  /* ── RENDER PRINCIPAL ── */
  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      {isActive && <TissBillingPanel key={currentEntidade?.id} />}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');`}</style>

      {/* ── CABEÇALHO ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>
            Gestão de Convênios
          </h5>
          <p style={{ margin: '2px 0 0', fontSize: `calc(12px * var(--font-scale))`, color: '#9ca3af' }}>
            {convenios.length} {convenios.length === 1 ? 'convênio cadastrado' : 'convênios cadastrados'}
          </p>
        </div>
        <button
          onClick={() => handleOpenConvenioModal()}
          style={{
            display: 'flex', alignItems: 'center', gap: '7px',
            padding: '9px 18px', borderRadius: '8px', border: 'none',
            background: 'linear-gradient(135deg, #065f46, #059669)',
            color: '#fff', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600',
            cursor: 'pointer', boxShadow: '0 2px 8px rgba(5,150,105,0.3)',
            fontFamily: 'inherit',
          }}
        >
          <Icon d={ICONS.plus} size={14} color="#fff" />
          Novo Convênio
        </button>
      </div>

      {/* ── LISTA DE CONVÊNIOS ── */}
      {convenios.length === 0 ? (
        <div style={{
          backgroundColor: '#fff', borderRadius: '12px',
          border: '1px dashed #e5e7eb', padding: '60px 24px', textAlign: 'center',
        }}>
          <Icon d={ICONS.shield} size={36} color="#d1d5db" />
          <p style={{ margin: '12px 0 0', fontSize: `calc(14px * var(--font-scale))`, fontWeight: '600', color: '#374151' }}>
            Nenhum convênio cadastrado
          </p>
        </div>
      ) : (
        convenios.map(convenio => (
          <AccordionItem
            key={convenio.id}
            title={convenio.nome}
            badge={`${convenio.planos?.length || 0} plano${(convenio.planos?.length || 0) !== 1 ? 's' : ''}`}
          >
            {/* Ações do convênio */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <button
                onClick={() => handleOpenConvenioModal(convenio)}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <Icon d={ICONS.edit} size={12} color="#374151" /> Editar Convênio
              </button>
              <button
                onClick={() => handleOpenPlanoModal(convenio.id)}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '6px', border: 'none', background: 'linear-gradient(135deg, #1e3a5f, #2563eb)', color: '#fff', fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <Icon d={ICONS.plus} size={12} color="#fff" /> Adicionar Plano
              </button>
            </div>

            {/* Planos */}
            {!convenio.planos?.length ? (
              <p style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#9ca3af', margin: 0 }}>Nenhum plano cadastrado.</p>
            ) : (
              convenio.planos.map(plano => (
                <AccordionItem
                  key={plano.id}
                  title={plano.nome}
                  badge={`${plano.coberturas?.length || 0} cobertura${(plano.coberturas?.length || 0) !== 1 ? 's' : ''}`}
                  accentColor="#7c3aed"
                >
                  {/* Ações do plano */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                    <button
                      onClick={() => handleOpenPlanoModal(convenio.id, plano)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      <Icon d={ICONS.edit} size={11} color="#374151" /> Editar Plano
                    </button>
                    <button
                      onClick={() => handleOpenCoberturaModal(plano.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '6px', border: 'none', backgroundColor: '#7c3aed', color: '#fff', fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      <Icon d={ICONS.plus} size={11} color="#fff" /> Adicionar Cobertura
                    </button>
                  </div>

                  {/* Tabela de coberturas */}
                  {!plano.coberturas?.length ? (
                    <p style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#9ca3af', margin: 0 }}>Nenhuma regra de cobertura cadastrada.</p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            {['Serviço', 'Coparticipação (Paciente)', 'Ações'].map(h => (
                              <th key={h} style={{ padding: '8px 12px', fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '2px solid #e2e8f0', textAlign: 'left', background: '#f8fafc' }}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {plano.coberturas.map(cobertura => (
                            <tr key={cobertura.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '10px 12px', fontSize: `calc(13px * var(--font-scale))`, color: '#374151', fontWeight: '500' }}>
                                {cobertura.servico?.nome || 'Serviço não encontrado'}
                              </td>
                              <td style={{ padding: '10px 12px', fontSize: `calc(13px * var(--font-scale))`, color: '#374151' }}>
                                <span style={{
                                  fontSize: `calc(12px * var(--font-scale))`, fontWeight: '700', padding: '2px 8px', borderRadius: '999px',
                                  backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0',
                                }}>
                                  {cobertura.tipo_coparticipacao === 'percentual'
                                    ? `${cobertura.valor_coparticipacao}%`
                                    : formatCurrency(cobertura.valor_coparticipacao)}
                                </span>
                              </td>
                              <td style={{ padding: '10px 12px' }}>
                                <button
                                  onClick={() => handleOpenCoberturaModal(plano.id, cobertura)}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
                                >
                                  <Icon d={ICONS.edit} size={11} color="#374151" /> Editar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </AccordionItem>
              ))
            )}
          </AccordionItem>
        ))
      )}

      {/* ════════ MODAL CONVÊNIO ════════ */}
      <ModalWrapper
        show={showConvenioModal}
        onHide={() => setShowConvenioModal(false)}
        title={currentConvenio?.id ? 'Editar Convênio' : 'Novo Convênio'}
        icon={ICONS.shield}
        onSave={handleSaveConvenio}
        isSaving={isSaving}
        error={modalError}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <FieldLabel required>Nome da Operadora</FieldLabel>
            <input style={inputStyle} type="text" value={currentConvenio?.nome || ''} onChange={e => setCurrentConvenio({ ...currentConvenio, nome: e.target.value })} required {...focusHandlers} />
          </div>
          <div>
            <FieldLabel>CNPJ</FieldLabel>
            <input style={inputStyle} type="text" value={currentConvenio?.cnpj || ''} onChange={e => setCurrentConvenio({ ...currentConvenio, cnpj: e.target.value })} {...focusHandlers} />
          </div>
          <div>
            <FieldLabel>Registro ANS</FieldLabel>
            <input style={inputStyle} type="text" value={currentConvenio?.registro_ans || ''} onChange={e => setCurrentConvenio({ ...currentConvenio, registro_ans: e.target.value })} {...focusHandlers} />
          </div>
        </div>
      </ModalWrapper>

      {/* ════════ MODAL PLANO ════════ */}
      <ModalWrapper
        show={showPlanoModal}
        onHide={() => setShowPlanoModal(false)}
        title={currentPlano?.id ? 'Editar Plano' : 'Novo Plano'}
        icon={ICONS.plan}
        onSave={handleSavePlano}
        isSaving={isSaving}
        error={modalError}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <FieldLabel required>Nome do Plano</FieldLabel>
            <input style={inputStyle} type="text" value={currentPlano?.nome || ''} onChange={e => setCurrentPlano({ ...currentPlano, nome: e.target.value })} required {...focusHandlers} />
          </div>
          <div>
            <FieldLabel>Descrição</FieldLabel>
            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '70px' }} value={currentPlano?.descricao || ''} onChange={e => setCurrentPlano({ ...currentPlano, descricao: e.target.value })} {...focusHandlers} />
          </div>
        </div>
      </ModalWrapper>

      {/* ════════ MODAL COBERTURA ════════ */}
      <ModalWrapper
        show={showCoberturaModal}
        onHide={() => setShowCoberturaModal(false)}
        title={currentCobertura?.id ? 'Editar Regra de Cobertura' : 'Nova Regra de Cobertura'}
        icon={ICONS.shield}
        onSave={handleSaveCobertura}
        isSaving={isSaving}
        error={modalError}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <FieldLabel required>Serviço</FieldLabel>
            <select
              style={{ ...inputStyle, appearance: 'auto', opacity: currentCobertura?.id ? 0.6 : 1 }}
              value={currentCobertura?.servico_id || ''}
              onChange={e => setCurrentCobertura({ ...currentCobertura, servico_id: e.target.value })}
              disabled={!!currentCobertura?.id}
              {...focusHandlers}
            >
              <option value="">Selecione um serviço...</option>
              {servicos.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
            {currentCobertura?.id && (
              <p style={{ margin: '4px 0 0', fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>
                O serviço não pode ser alterado após a criação da regra.
              </p>
            )}
          </div>

          <div style={{ height: '1px', backgroundColor: '#f1f5f9' }} />

          <p style={{ margin: 0, fontSize: `calc(12px * var(--font-scale))`, fontWeight: '700', color: '#374151' }}>
            Regra de Coparticipação (Valor pago pelo Paciente)
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <FieldLabel>Tipo de Coparticipação</FieldLabel>
              <select
                style={{ ...inputStyle, appearance: 'auto' }}
                value={currentCobertura?.tipo_coparticipacao || 'percentual'}
                onChange={e => setCurrentCobertura({ ...currentCobertura, tipo_coparticipacao: e.target.value })}
                {...focusHandlers}
              >
                <option value="percentual">Percentual (%)</option>
                <option value="valor_fixo">Valor Fixo (R$)</option>
              </select>
            </div>
            <div>
              <FieldLabel>Valor da Coparticipação</FieldLabel>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {currentCobertura?.tipo_coparticipacao === 'valor_fixo' && (
                  <span style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280', fontWeight: '600' }}>R$</span>
                )}
                <input
                  style={inputStyle} type="number" step="0.01"
                  value={currentCobertura?.valor_coparticipacao || 0}
                  onChange={e => setCurrentCobertura({ ...currentCobertura, valor_coparticipacao: e.target.value })}
                  {...focusHandlers}
                />
                {currentCobertura?.tipo_coparticipacao === 'percentual' && (
                  <span style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280', fontWeight: '600' }}>%</span>
                )}
              </div>
            </div>
          </div>

          {/* Dica */}
          <div style={{ display: 'flex', gap: '8px', padding: '10px 14px', borderRadius: '8px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
            <Icon d={ICONS.info} size={14} color="#2563eb" />
            <p style={{ margin: 0, fontSize: `calc(12px * var(--font-scale))`, color: '#1e40af', lineHeight: '1.5' }}>
              Exemplo: Se a coparticipação for de <strong>20%</strong>, o paciente pagará 20% do valor do serviço e o convênio cobrirá os 80% restantes.
            </p>
          </div>
        </div>
      </ModalWrapper>
    </div>
  );
};

/* ── Modal reutilizável ── */
const ModalWrapper = ({ show, onHide, title, icon, onSave, isSaving, error, children }) => (
  <Modal show={show} onHide={onHide} centered>
    <Modal.Header style={{ padding: 0, border: 'none' }}>
      <div style={{
        width: '100%', padding: '18px 22px',
        background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
        display: 'flex', alignItems: 'center', gap: '12px',
      }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '9px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon d={icon} size={16} color="#fff" />
        </div>
        <span style={{ flex: 1, fontSize: `calc(14px * var(--font-scale))`, fontWeight: '700', color: '#fff' }}>{title}</span>
        <button onClick={onHide} style={{ width: '26px', height: '26px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.3)', backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', lineHeight: 1, flexShrink: 0 }}>×</button>
      </div>
    </Modal.Header>
    <Modal.Body style={{ padding: '20px 22px', backgroundColor: '#f9fafb' }}>
      {error && (
        <div style={{ padding: '10px 14px', borderRadius: '8px', marginBottom: '14px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: `calc(13px * var(--font-scale))` }}>
          {error}
        </div>
      )}
      {children}
    </Modal.Body>
    <Modal.Footer style={{ padding: '14px 22px', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
      <button onClick={onHide} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500', fontFamily: 'inherit' }}>
        Cancelar
      </button>
      <button onClick={onSave} disabled={isSaving} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 18px', borderRadius: '8px', border: 'none', background: isSaving ? '#93c5fd' : 'linear-gradient(135deg, #1e3a5f, #2563eb)', color: '#fff', cursor: isSaving ? 'not-allowed' : 'pointer', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', fontFamily: 'inherit' }}>
        {isSaving && <Spinner as="span" animation="border" size="sm" style={{ width: '13px', height: '13px', borderWidth: '2px' }} />}
        {isSaving ? 'Salvando...' : 'Salvar'}
      </button>
    </Modal.Footer>
  </Modal>
);

export default ConveniosTab;
